// Tax-profile UI copy (en / es / zh).
//
// English copy mirrors the IRS standard wording for W-9 / W-8BEN.
// Spanish uses the formal "usted" register (matches Latin-American +
// Castilian VO industry norms — see tone rule #8 in types.ts).
// Chinese is Simplified (zh-Hans).
//
// Tone rules apply (no warning red, first-person, give the reason). Tax
// language is necessarily formal; we soften it where possible (e.g. "We use
// this for your 1099 at year end" rather than "Required for tax reporting").
/** Section headings on the tax tab. */
export const SECTION_HEADINGS = {
    taxForm: {
        en: 'Tax form',
        es: 'Formulario fiscal',
        zh: '税务表格',
    },
    taxId: {
        en: 'Tax ID',
        es: 'Identificación fiscal',
        zh: '税号',
    },
    loanOut: {
        en: 'Loan-out company (optional)',
        es: 'Empresa intermediaria (opcional)',
        zh: '签约公司(可选)',
    },
    payee: {
        en: 'Payee information',
        es: 'Datos del beneficiario',
        zh: '收款人信息',
    },
    contact: {
        en: 'Contact',
        es: 'Contacto',
        zh: '联系方式',
    },
};
/** Form-type select options. The internal value matches the API enum. */
export const FORM_TYPE_OPTIONS = {
    w9: {
        en: 'W-9 (US individual or business)',
        es: 'W-9 (persona o empresa de EE. UU.)',
        zh: 'W-9(美国个人或企业)',
    },
    w8ben: {
        en: 'W-8BEN (foreign individual)',
        es: 'W-8BEN (persona extranjera)',
        zh: 'W-8BEN(外籍个人)',
    },
    w8ben_e: {
        en: 'W-8BEN-E (foreign entity)',
        es: 'W-8BEN-E (entidad extranjera)',
        zh: 'W-8BEN-E(外国实体)',
    },
};
/** Field labels, organized loosely by section. */
export const LABELS = {
    // Tax form section
    chooseFormType: {
        en: 'Which form do you need to submit?',
        es: '¿Qué formulario necesita enviar?',
        zh: '您需要提交哪种表格?',
    },
    uploadForm: {
        en: 'Upload signed PDF',
        es: 'Subir PDF firmado',
        zh: '上传已签署的 PDF',
    },
    replaceForm: {
        en: 'Replace form',
        es: 'Reemplazar formulario',
        zh: '替换表格',
    },
    viewForm: {
        en: 'View',
        es: 'Ver',
        zh: '查看',
    },
    pdfHint: {
        en: 'PDF only, 1 KB to 5 MB. We keep this private and only share with the IRS at year end.',
        es: 'Solo PDF, de 1 KB a 5 MB. Lo guardamos en privado y solo se comparte con el IRS al final del año.',
        zh: '仅支持 PDF, 1 KB 至 5 MB。我们妥善保密,仅在年底与 IRS 共享。',
    },
    formOnFileSince: {
        en: 'On file since',
        es: 'En archivo desde',
        zh: '上传于',
    },
    sha256Label: {
        en: 'sha256',
        es: 'sha256',
        zh: 'sha256',
    },
    // Tax ID section
    ssnLabel: {
        en: 'Social Security Number',
        es: 'Número de Seguro Social',
        zh: '社会安全号 (SSN)',
    },
    ssnHint: {
        en: 'We encrypt this on save. Producers never see the full number — only the last 4 digits.',
        es: 'Lo ciframos al guardarlo. Los productores nunca ven el número completo, solo los últimos 4 dígitos.',
        zh: '保存时会加密。制片方只能看到后 4 位,不会看到完整号码。',
    },
    einLabel: {
        en: 'Employer Identification Number (EIN)',
        es: 'Número de Identificación del Empleador (EIN)',
        zh: '雇主识别号 (EIN)',
    },
    einHint: {
        en: 'Use this when the 1099 should be issued to a corporation or LLC instead of you personally.',
        es: 'Úselo cuando el 1099 deba emitirse a una empresa o LLC en lugar de a usted personalmente.',
        zh: '若 1099 需开给公司或 LLC 而非个人,请填写此处。',
    },
    updateButton: {
        en: 'Update',
        es: 'Actualizar',
        zh: '更新',
    },
    saveButton: {
        en: 'Save',
        es: 'Guardar',
        zh: '保存',
    },
    cancelButton: {
        en: 'Cancel',
        es: 'Cancelar',
        zh: '取消',
    },
    // Loan-out + payee
    loanOutName: {
        en: 'Company name',
        es: 'Nombre de la empresa',
        zh: '公司名称',
    },
    loanOutEin: {
        en: 'Company EIN',
        es: 'EIN de la empresa',
        zh: '公司 EIN',
    },
    payeeLegalName: {
        en: 'Legal name on check / 1099',
        es: 'Nombre legal en el cheque / 1099',
        zh: '支票/1099 上的法定姓名',
    },
    payeeLegalNameHint: {
        en: 'Use the exact name on your tax records — this is what appears on the check.',
        es: 'Use el nombre exacto que figura en sus registros fiscales: es el que aparece en el cheque.',
        zh: '请使用与税务记录完全一致的姓名 —— 支票上将以此姓名签发。',
    },
    addressLine1: {
        en: 'Street address',
        es: 'Domicilio',
        zh: '街道地址',
    },
    addressLine2: {
        en: 'Apt / suite (optional)',
        es: 'Apto / suite (opcional)',
        zh: '公寓/单元(可选)',
    },
    city: {
        en: 'City',
        es: 'Ciudad',
        zh: '城市',
    },
    stateRegion: {
        en: 'State / region',
        es: 'Estado / región',
        zh: '州/省',
    },
    postal: {
        en: 'ZIP / postal code',
        es: 'Código postal',
        zh: '邮编',
    },
    country: {
        en: 'Country (2-letter)',
        es: 'País (2 letras)',
        zh: '国家(两位代码)',
    },
    phone: {
        en: 'Phone',
        es: 'Teléfono',
        zh: '电话',
    },
    alternateEmail: {
        en: 'Alternate email (optional)',
        es: 'Correo alternativo (opcional)',
        zh: '备用邮箱(可选)',
    },
    backupWithholding: {
        en: 'I am subject to backup withholding (W-9 box 4)',
        es: 'Estoy sujeto a retención de respaldo (W-9 casilla 4)',
        zh: '我适用预扣备款(W-9 第 4 项)',
    },
    foreignCountry: {
        en: 'Country of residence',
        es: 'País de residencia',
        zh: '居住国家',
    },
    foreignTreaty: {
        en: 'Treaty article + rate (if claiming reduced withholding)',
        es: 'Artículo y tasa del tratado (si solicita retención reducida)',
        zh: '税收协定条款及税率(如申请减税)',
    },
};
/** ESIGN consent block — perjury statements are form-specific. */
export const ESIGN = {
    perjuryHeadingW9: {
        en: 'Certification (Form W-9)',
        es: 'Certificación (Formulario W-9)',
        zh: '声明(表格 W-9)',
    },
    perjuryBodyW9: {
        en: 'Under penalties of perjury, I certify that the number shown above is my correct taxpayer identification number, that I am not subject to backup withholding (unless I checked the box above), and that I am a U.S. person as defined by the IRS.',
        es: 'Bajo pena de perjurio, certifico que el número mostrado arriba es mi número de identificación fiscal correcto, que no estoy sujeto a retención de respaldo (salvo que haya marcado la casilla anterior) y que soy una persona de EE. UU. según la definición del IRS.',
        zh: '本人在伪证罪处罚之下声明:上方所列号码为本人正确的纳税人识别号;本人不适用预扣备款(除非上方已勾选);本人为 IRS 所定义的美国人。',
    },
    perjuryHeadingW8: {
        en: 'Certification (Form W-8BEN)',
        es: 'Certificación (Formulario W-8BEN)',
        zh: '声明(表格 W-8BEN)',
    },
    perjuryBodyW8: {
        en: 'Under penalties of perjury, I certify that I am the beneficial owner of the income to which this form relates, that I am not a U.S. person, and that the information on this form is true, correct, and complete.',
        es: 'Bajo pena de perjurio, certifico que soy el beneficiario efectivo de los ingresos a los que se refiere este formulario, que no soy una persona de EE. UU. y que la información de este formulario es verdadera, correcta y completa.',
        zh: '本人在伪证罪处罚之下声明:本人是本表格所涉收入的实际受益人,本人非美国人,本表格所载信息真实、正确且完整。',
    },
    typeLegalNamePrompt: {
        en: 'Type your full legal name to sign',
        es: 'Escriba su nombre legal completo para firmar',
        zh: '请输入您的法定全名以签署',
    },
    typeLegalNameHint: {
        en: 'Must match the legal name above exactly. We log the timestamp, IP, and browser as your signature.',
        es: 'Debe coincidir exactamente con el nombre legal anterior. Registramos la fecha, la IP y el navegador como firma.',
        zh: '需与上方法定姓名完全一致。我们将记录时间戳、IP 与浏览器作为签名凭证。',
    },
    namePlaceholder: {
        en: 'e.g. Alice Q. Actor',
        es: 'p. ej., Alice Q. Actor',
        zh: '例如:张三',
    },
    legalNameMissing: {
        en: 'Fill in your legal name in the Payee section above before signing.',
        es: 'Complete su nombre legal en la sección "Beneficiario" antes de firmar.',
        zh: '请先在上方"收款人"部分填写法定姓名后再签署。',
    },
};
/** Generic action / status messages. */
export const MESSAGES = {
    saved: {
        en: 'Tax profile saved.',
        es: 'Perfil fiscal guardado.',
        zh: '税务资料已保存。',
    },
    saveFailed: {
        en: 'Could not save tax profile.',
        es: 'No se pudo guardar el perfil fiscal.',
        zh: '保存税务资料失败。',
    },
    loading: {
        en: 'Loading tax profile...',
        es: 'Cargando perfil fiscal...',
        zh: '正在加载税务资料...',
    },
    signRequired: {
        en: 'Please type your legal name above to sign.',
        es: 'Escriba su nombre legal arriba para firmar.',
        zh: '请在上方输入法定姓名以签署。',
    },
    uploadFailed: {
        en: 'Upload failed — please try again.',
        es: 'Error al subir — vuelva a intentarlo.',
        zh: '上传失败,请重试。',
    },
    invalidSsn: {
        en: 'SSN must be 9 digits.',
        es: 'El SSN debe tener 9 dígitos.',
        zh: 'SSN 必须为 9 位数字。',
    },
    invalidEin: {
        en: 'EIN must be 9 digits.',
        es: 'El EIN debe tener 9 dígitos.',
        zh: 'EIN 必须为 9 位数字。',
    },
};
